import { useEffect, useState, type JSX } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { Role } from '@cca/corpus-shared';
import { api, ApiError, type CanonicalDocument } from '../api/client';
import { useAppContext } from '../frame/AppShell';
import { ReadingView, type PrintMode, type PrintLayout } from './ReadingView';

/** Single-document reading view. */
export function DocumentPage(): JSX.Element {
  const { session } = useAppContext();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const printParam = searchParams.get('print');
  const printMode: PrintMode =
    printParam === 'clean' ? 'clean' : printParam === 'annotated' ? 'annotated' : null;
  const printLayout: PrintLayout = searchParams.get('layout') === 'gloss' ? 'gloss' : 'notes';
  const printAsParam = searchParams.get('as');
  const printAs: Role | undefined =
    printAsParam === 'commission' || printAsParam === 'clergy' || printAsParam === 'public'
      ? printAsParam
      : undefined;
  const autoPrint = searchParams.get('autoprint') === '1';
  const [doc, setDoc] = useState<CanonicalDocument | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    setDoc(null);
    setError(null);
    setNotFound(false);
    api
      .document(id)
      .then((d) => alive && setDoc(d))
      .catch((e: unknown) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setError(e instanceof Error ? e.message : 'Unknown error');
      });
    return () => {
      alive = false;
    };
  }, [id]);

  // Re-fetch after an authoring change so the surface reflects the new state.
  const reload = async () => {
    if (!id) return;
    setDoc(await api.document(id));
  };

  if (doc)
    return (
      <ReadingView
        doc={doc}
        tier={session.tier}
        onReload={reload}
        printMode={printMode}
        printLayout={printLayout}
        printAs={printAs}
        autoPrint={autoPrint}
      />
    );

  return (
    <div className="stack">
      <Link to="/read" className="back-link">
        ← All documents
      </Link>
      {error && <div className="alert alert-danger">{error}</div>}
      {notFound && (
        <div className="card">
          <p style={{ margin: 0 }}>That document doesn’t exist.</p>
        </div>
      )}
      {!error && !notFound && <p className="muted">Loading…</p>}
    </div>
  );
}
